export function mapCampaign(row) {
  let content = {}
  let tracking = {}
  try {
    content = typeof row.content === 'string' ? JSON.parse(row.content) : row.content || {}
  } catch {
    content = {}
  }
  try {
    tracking = typeof row.tracking_data === 'string' ? JSON.parse(row.tracking_data) : row.tracking_data || {}
  } catch {
    tracking = {}
  }
  const sent = Number(tracking.sent || 0)
  const failed = Number(tracking.failed || 0)
  const delivered = Number(tracking.delivered ?? Math.max(0, sent - failed))
  return {
    id: row.id,
    name: row.title,
    subject: content.subject || row.title,
    status: row.status,
    audience: row.target_audience,
    audienceCount: row.target_count || tracking.recipientCount,
    senderDisplayName: row.sender_display_name || '',
    userFeedback: row.user_feedback || '',
    adminNotes: row.admin_notes || '',
    sentAt: row.sent_at || row.reviewed_at,
    createdAt: row.created_at,
    stats: sent > 0
      ? {
          sent,
          delivered,
          opened: Number(tracking.opened || 0),
          clicked: Number(tracking.clicked || 0),
          bounced: failed,
        }
      : null,
    openRate: sent > 0 ? Math.round((Number(tracking.opened || 0) / sent) * 100) : null,
    clickRate: sent > 0 ? Math.round((Number(tracking.clicked || 0) / sent) * 100) : null,
    body: content.body || content.body_html || '',
  }
}

export function mapMeeting(row) {
  const start = row.start_time ? new Date(row.start_time) : null
  let participants = []
  try {
    participants = typeof row.participants === 'string' ? JSON.parse(row.participants) : row.participants || []
  } catch {
    participants = []
  }
  const statusMap = { scheduled: 'scheduled', live: 'live', ended: 'completed', upcoming: 'scheduled' }
  return {
    id: row.id,
    title: row.subject,
    status: statusMap[row.status] || row.status || 'scheduled',
    start,
    date: start ? start.toISOString().slice(0, 10) : '',
    time: start ? start.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : '',
    duration: row.duration || 60,
    participants,
    joinUrl: row.join_url,
    description: row.description || row.notes || '',
    provider: row.provider,
  }
}

export function formatFileSize(bytes) {
  if (!bytes) return '—'
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

export const TRANSLATE_LANG_OPTIONS = [
  { code: 'en', label: 'English', flag: '🇬🇧' },
  { code: 'zh', label: 'Chinese (Simplified)', flag: '🇨🇳' },
  { code: 'zh-tw', label: 'Chinese (Traditional)', flag: '🇹🇼' },
  { code: 'ja', label: 'Japanese', flag: '🇯🇵' },
  { code: 'ko', label: 'Korean', flag: '🇰🇷' },
  { code: 'fr', label: 'French', flag: '🇫🇷' },
  { code: 'de', label: 'German', flag: '🇩🇪' },
  { code: 'es', label: 'Spanish', flag: '🇪🇸' },
  { code: 'pt', label: 'Portuguese', flag: '🇵🇹' },
  { code: 'it', label: 'Italian', flag: '🇮🇹' },
  { code: 'nl', label: 'Dutch', flag: '🇳🇱' },
  { code: 'ru', label: 'Russian', flag: '🇷🇺' },
  { code: 'ar', label: 'Arabic', flag: '🇸🇦' },
  { code: 'th', label: 'Thai', flag: '🇹🇭' },
  { code: 'vi', label: 'Vietnamese', flag: '🇻🇳' },
  { code: 'id', label: 'Indonesian', flag: '🇮🇩' },
  { code: 'ms', label: 'Malay', flag: '🇲🇾' },
  { code: 'hi', label: 'Hindi', flag: '🇮🇳' },
  { code: 'bn', label: 'Bengali', flag: '🇧🇩' },
  { code: 'ta', label: 'Tamil', flag: '🇮🇳' },
  { code: 'te', label: 'Telugu', flag: '🇮🇳' },
  { code: 'mr', label: 'Marathi', flag: '🇮🇳' },
  { code: 'gu', label: 'Gujarati', flag: '🇮🇳' },
  { code: 'kn', label: 'Kannada', flag: '🇮🇳' },
  { code: 'ml', label: 'Malayalam', flag: '🇮🇳' },
  { code: 'pa', label: 'Punjabi', flag: '🇮🇳' },
  { code: 'ur', label: 'Urdu', flag: '🇵🇰' },
  { code: 'tr', label: 'Turkish', flag: '🇹🇷' },
  { code: 'pl', label: 'Polish', flag: '🇵🇱' },
  { code: 'uk', label: 'Ukrainian', flag: '🇺🇦' },
  { code: 'cs', label: 'Czech', flag: '🇨🇿' },
  { code: 'ro', label: 'Romanian', flag: '🇷🇴' },
  { code: 'hu', label: 'Hungarian', flag: '🇭🇺' },
  { code: 'el', label: 'Greek', flag: '🇬🇷' },
  { code: 'he', label: 'Hebrew', flag: '🇮🇱' },
  { code: 'fa', label: 'Persian', flag: '🇮🇷' },
  { code: 'sv', label: 'Swedish', flag: '🇸🇪' },
  { code: 'no', label: 'Norwegian', flag: '🇳🇴' },
  { code: 'da', label: 'Danish', flag: '🇩🇰' },
  { code: 'fi', label: 'Finnish', flag: '🇫🇮' },
  { code: 'sk', label: 'Slovak', flag: '🇸🇰' },
  { code: 'bg', label: 'Bulgarian', flag: '🇧🇬' },
  { code: 'hr', label: 'Croatian', flag: '🇭🇷' },
  { code: 'sr', label: 'Serbian', flag: '🇷🇸' },
  { code: 'sl', label: 'Slovenian', flag: '🇸🇮' },
  { code: 'lt', label: 'Lithuanian', flag: '🇱🇹' },
  { code: 'lv', label: 'Latvian', flag: '🇱🇻' },
  { code: 'et', label: 'Estonian', flag: '🇪🇪' },
  { code: 'ca', label: 'Catalan', flag: '🇪🇸' },
  { code: 'fil', label: 'Filipino', flag: '🇵🇭' },
  { code: 'sw', label: 'Swahili', flag: '🇰🇪' },
  { code: 'af', label: 'Afrikaans', flag: '🇿🇦' },
]

export const LANG_FLAGS = Object.fromEntries(TRANSLATE_LANG_OPTIONS.map(({ code, flag }) => [code, flag]))

export const LANG_LABELS = Object.fromEntries(TRANSLATE_LANG_OPTIONS.map(({ code, label }) => [code, label]))

export const TRANSLATION_STATUS = {
  ready: { color: 'ready', label: 'Translated' },
  failed: { color: 'failed', label: 'Translation failed' },
  in_progress: { color: 'pending', label: 'In Progress' },
  pending: { color: 'pending', label: 'Pending' },
}

function parseFileTranslations(row) {
  let translations = {}
  try {
    translations = typeof row.translations === 'string' ? JSON.parse(row.translations) : row.translations || {}
  } catch {
    translations = {}
  }
  const trStatus = {}
  const translationTexts = {}
  Object.entries(translations).forEach(([lang, text]) => {
    translationTexts[lang] = text
    if (!text) trStatus[lang] = 'pending'
    else if (/^【/.test(String(text).trim())) trStatus[lang] = 'failed'
    else trStatus[lang] = 'ready'
  })
  return { translations: trStatus, translationTexts }
}

const IMAGE_EXTENSIONS = new Set(['png', 'jpg', 'jpeg', 'gif', 'webp', 'bmp', 'svg'])

export function isImageFile(file) {
  const name = file?.name || ''
  const ext = name.split('.').pop()?.toLowerCase() || ''
  if (IMAGE_EXTENSIONS.has(ext)) return true
  const mime = file?.mimeType || file?.mime_type || ''
  return typeof mime === 'string' && mime.startsWith('image/')
}

export const FOLDER_MATCH = {
  nda: /nda|confidential/i,
  dd: /due diligence|dd_|diligence/i,
  fund: /fund|portfolio/i,
  legal: /legal|term sheet|agreement/i,
  finance: /financial|report|xls|xlsx/i,
  // Upload handlers strip path segments — match prefix and legacy chat screenshot names
  screenshots: /^screenshots[\/_]|^screenshot[_-]|^chat_[\w.-]+_\d{10,}\.png$/i,
  share: /share|deal/i,
  archive: /archive|old/i,
}

export function fileMatchesFolder(fileName, folderId) {
  const re = FOLDER_MATCH[folderId]
  return re ? re.test(fileName || '') : false
}

export function mapFileRow(row) {
  const ext = (row.original_name || row.name || '').split('.').pop()?.toLowerCase() || 'pdf'
  const typeMap = {
    pdf: 'pdf', doc: 'doc', docx: 'doc', xls: 'xls', xlsx: 'xls', ppt: 'ppt', pptx: 'ppt',
    png: 'png', jpg: 'png', jpeg: 'png', gif: 'png', webp: 'png', bmp: 'png', svg: 'png',
  }
  const { translations, translationTexts } = parseFileTranslations(row)
  return {
    id: row.id,
    name: row.original_name || row.name,
    type: typeMap[ext] || 'pdf',
    mimeType: row.mime_type || '',
    size: formatFileSize(row.size),
    sizeBytes: row.size || 0,
    uploadedBy: row.uploader_name || 'User',
    uploadedAt: row.created_at ? String(row.created_at).slice(0, 10) : '',
    isConfidential: !!row.is_confidential,
    deal: row.deal_id || '',
    folderId: row.folder_id || '',
    url: row.downloadUrl || row.url,
    translations,
    translationTexts,
  }
}

export function emailMatchesFolder(email, folderId) {
  if (folderId === 'starred') return !!email.starred
  if (folderId === 'inbox') return email.folder === 'inbox'
  return email.folder === folderId
}

export function filterMailboxEmails(emails, { folder, accountId = null, search = '', listFilter = 'all' }) {
  const q = search.trim().toLowerCase()
  return emails.filter((e) => {
    if (accountId && e.account !== accountId) return false
    if (!emailMatchesFolder(e, folder)) return false
    if (listFilter === 'unread' && !e.unread) return false
    if (listFilter === 'starred' && !e.starred) return false
    if (listFilter === 'attachments' && !e.tags?.includes('attachment')) return false
    if (!q) return true
    return e.subject.toLowerCase().includes(q) || e.sender.toLowerCase().includes(q)
  })
}

export function countFolderEmails(emails, folderId, accountId = null) {
  const scoped = accountId ? emails.filter((e) => e.account === accountId) : emails
  if (folderId === 'inbox') {
    return scoped.filter((e) => e.folder === 'inbox' && e.unread).length
  }
  if (folderId === 'drafts') {
    return scoped.filter((e) => e.folder === 'drafts').length
  }
  return 0
}

export const FILE_FOLDERS = [
  { id: 'nda', icon: 'clipboard', cls: 'nda', name: 'NDA Documents' },
  { id: 'dd', icon: 'search', cls: 'dd', name: 'Due Diligence' },
  { id: 'fund', icon: 'dollar', cls: 'fund', name: 'Fund Documents' },
  { id: 'legal', icon: 'dispute', cls: 'legal', name: 'Legal' },
  { id: 'finance', icon: 'chart', cls: 'finance', name: 'Financial Reports' },
  { id: 'screenshots', icon: 'image', cls: 'screenshots', name: 'Screenshots' },
  { id: 'share', icon: 'collaborate', cls: 'share', name: 'Deal Sharing' },
  { id: 'archive', icon: 'archive', cls: 'archive', name: 'Archive' },
]
